import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Image,
  Input,
  message,
  Modal,
  Pagination,
  Select,
  Space,
  Steps,
  Table,
  Tabs,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useSnapshot } from 'valtio';
import {
  downloadOrderImportTemplateApi,
  exportPhysicalOrderApi,
  getOrderDetailApi,
  importPhysicalOrderApi,
  searchOrderListApi,
  shipOrderApi,
} from '@/api/pointCenter/order';
import { ORDER_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;

const statusMap: Record<string, string> = {
  TO_EXCHANGE: '去兑换',
  WAIT_PAY: '待付款',
  PENDING_SHIPMENT: '待发货',
  SHIPPED: '已发货',
  FINISHED: '已完成',
  REFUND: '退款/售后',
};

const equityTypeMap: Record<string, string> = {
  POINTS_EXCHANGE: '积分兑换',
  MEMBER_BENEFIT: '会员权益',
};

const descLabelStyle: React.CSSProperties = { width: 120 };
const descContentStyle: React.CSSProperties = { wordBreak: 'break-all', width: 170 };

type OrderListProps = {
  overrideShopId?: number | null;
  fixedUserId?: string | null;
  hideImportExport?: boolean;
  hideUserFilters?: boolean;
  noPagePadding?: boolean;
};

const OrderList: React.FC<OrderListProps> = ({
  overrideShopId,
  fixedUserId,
  hideImportExport = false,
  hideUserFilters = false,
  noPagePadding = false,
}) => {
  const [form] = Form.useForm();
  const [shipForm] = Form.useForm();
  const { currentShopId, shopList } = useSnapshot(state);
  const resolvedShopId = overrideShopId ?? currentShopId;
  const shopNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (shopList || []).forEach((shop: any) => {
      const shopId = shop?.shopId ?? shop?.platformShopId;
      if (shopId !== undefined && shopId !== null) {
        map.set(String(shopId), shop?.shopName || '');
      }
    });
    return map;
  }, [shopList]);
  const getShopDisplayName = (shopId: any) => {
    if (shopId === undefined || shopId === null || shopId === '') return '--';
    return shopNameMap.get(String(shopId)) || String(shopId);
  };
  const [activeKey, setActiveKey] = useState('physical');
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);
  const [shipVisible, setShipVisible] = useState(false);
  const [shipTarget, setShipTarget] = useState<any>(null);
  const [shipLoading, setShipLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [statusOptions] = useState([
    { label: '去兑换', value: 'TO_EXCHANGE' },
    { label: '待付款', value: 'WAIT_PAY' },
    { label: '待发货', value: 'PENDING_SHIPMENT' },
    { label: '已发货', value: 'SHIPPED' },
    { label: '已完成', value: 'FINISHED' },
    { label: '退款/售后', value: 'REFUND' },
  ]);
  const [equityTypeOptions] = useState([
    { label: '积分兑换', value: 'POINTS_EXCHANGE' },
    { label: '会员权益', value: 'MEMBER_BENEFIT' },
  ]);

  useEffect(() => {
    fetchList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedShopId, activeKey]);

  const buildFilterParams = () => {
    const values = form.getFieldsValue();
    const timeRange: Dayjs[] | undefined = values.createTime;
    return {
      transactionId: values.transactionId || undefined,
      recipientPhone: values.recipientPhone || undefined,
      userId: fixedUserId || (hideUserFilters ? undefined : values.userId) || undefined,
      userNick: hideUserFilters ? undefined : values.userNick || undefined,
      shippingStatus: values.shippingStatus || undefined,
      equityType: values.equityType || undefined,
      instanceType: activeKey === 'coupon' ? 'COUPON' : activeKey === 'thirdParty' ? 'THIRD_PARTY' : 'PRODUCT',
      exchangeTimeStart: timeRange?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
      exchangeTimeEnd: timeRange?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
    };
  };

  const fetchList = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    if (!resolvedShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const params: any = {
      ...buildFilterParams(),
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setLoading(true);
    try {
      const res = await searchOrderListApi(resolvedShopId, params);
      if (res?.code === 200) {
        setDataSource(res.rows || res.data?.rows || res.data || []);
        setTotal(res.total || res.data?.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (record: any) => {
    if (!resolvedShopId) return;
    try {
      const res = await getOrderDetailApi(resolvedShopId, record.id);
      if (res?.code === 200) {
        setDetailData(res.data || record);
        setDetailVisible(true);
      }
    } catch (error) {}
  };

  const openShip = async (record: any) => {
    if (!resolvedShopId) return;
    try {
      const res = await getOrderDetailApi(resolvedShopId, record.id);
      if (res?.code === 200) {
        setShipTarget(res.data || record);
        shipForm.resetFields();
        setShipVisible(true);
      }
    } catch (error) {}
  };

  const handleShipSubmit = async () => {
    if (!resolvedShopId || !shipTarget?.id) {
      setShipVisible(false);
      return;
    }
    try {
      const values = await shipForm.validateFields();
      setShipLoading(true);
      const res = await shipOrderApi(resolvedShopId, {
        shopId: resolvedShopId,
        orderId: shipTarget.id,
        logisticsCompany: values.logisticsCompany,
        logisticsNumber: values.logisticsNumber,
      });
      if (res?.code === 200) {
        message.success('发货成功');
        setShipVisible(false);
        fetchList();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
    } finally {
      setShipLoading(false);
    }
  };

  const handleImportClick = () => {
    if (!resolvedShopId) {
      message.warning('请先选择店铺');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !resolvedShopId) return;
    setImportLoading(true);
    try {
      const res = await importPhysicalOrderApi(resolvedShopId, file);
      if (res?.code === 200) {
        message.success(`导入成功`);
        fetchList({ pageNum: 1 });
      }
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!resolvedShopId) {
      message.warning('请先选择店铺');
      return;
    }
    await downloadOrderImportTemplateApi(resolvedShopId);
  };

  const handleExport = async () => {
    if (activeKey !== 'physical') return;
    if (!resolvedShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const params = buildFilterParams();
    await exportPhysicalOrderApi(resolvedShopId, params);
  };

  return (
    <div className={noPagePadding ? undefined : styles.page}>
      <Tabs
        activeKey={activeKey}
        onChange={key => setActiveKey(key)}
        items={[
          { key: 'physical', label: '实物' },
          { key: 'coupon', label: '店铺优惠券' },
          { key: 'thirdParty', label: '三方平台兑换商品' },
        ]}
      />
      <div className={styles.filterCard}>
        <Form
          form={form}
          layout="inline"
          className={styles.filterForm}>
          <Form.Item
            label="订单编号"
            name="transactionId">
            <Input
              style={{ width: 200 }}
              placeholder="请输入"
              allowClear
            />
          </Form.Item>
          {!hideUserFilters ? (
            <>
              <Form.Item
                label="平台ID"
                name="userId">
                <Input
                  style={{ width: 200 }}
                  placeholder="请输入"
                  allowClear
                />
              </Form.Item>
              <Form.Item
                label="用户昵称"
                name="userNick">
                <Input
                  style={{ width: 200 }}
                  placeholder="请输入"
                  allowClear
                />
              </Form.Item>
            </>
          ) : null}
          <Form.Item
            label="订单状态"
            name="shippingStatus">
            <Select
              style={{ width: 200 }}
              allowClear
              placeholder="全部"
              className={styles.select}
              options={statusOptions}
            />
          </Form.Item>
          <Form.Item
            label="权益类型"
            name="equityType">
            <Select
              style={{ width: 200 }}
              allowClear
              placeholder="全部"
              className={styles.select}
              options={equityTypeOptions}
            />
          </Form.Item>
          <Form.Item
            label="下单时间"
            name="createTime">
            <RangePicker
              showTime={{
                defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
              }}
              className={styles.range}
            />
          </Form.Item>
          <Form.Item className={styles.actionItem}>
            <Space size={12}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => fetchList({ pageNum: 1 })}>
                查询
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  form.resetFields();
                  fetchList({ pageNum: 1 });
                }}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {activeKey === 'physical' && !hideImportExport && (
        <div className={styles.toolbar}>
          <Space>
            {checkPermissions(ORDER_KEY.PRODUCT_EXPORT) && <Button onClick={handleExport}>导出所选订单</Button>}
            {checkPermissions(ORDER_KEY.PRODUCT_IMPORT) && (
              <Button
                onClick={handleImportClick}
                loading={importLoading}>
                导入订单发货信息
              </Button>
            )}
            {checkPermissions(ORDER_KEY.PRODUCT_TEMPLATE) && (
              <Button
                hidden
                onClick={handleDownloadTemplate}>
                下载订单发货信息模板
              </Button>
            )}
          </Space>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            style={{ display: 'none' }}
            onChange={handleImportChange}
          />
        </div>
      )}

      <div className={styles.listWrapper}>
        <div className={styles.listHeader}>
          <div className={`${styles.headerCell} ${styles.colProduct}`}>商品信息</div>
          <div className={`${styles.headerCell} ${styles.colCustomer}`}>客户信息</div>
          <div className={`${styles.headerCell} ${styles.colAmount}`}>实付积分</div>
          <div className={`${styles.headerCell} ${styles.colStatus}`}>状态</div>
          <div className={`${styles.headerCell} ${styles.colAction}`}>操作</div>
        </div>
        <Table
          rowKey={record => record.id || record.transactionId}
          dataSource={dataSource}
          loading={loading}
          pagination={false}
          showHeader={false}
          columns={
            useMemo<ColumnsType<any>>(
              () => [
                {
                  key: 'content',
                  className: styles.listCell,
                  render: (_: any, item: any) => (
                    <div className={styles.listItem}>
                      <div className={styles.itemHeader}>
                        <span>订单编号：{item.transactionId || '--'}</span>
                        <span>下单时间：{item.exchangeTime || '--'}</span>
                        <span>
                          资产类型：
                          {activeKey === 'coupon'
                            ? '店铺优惠券'
                            : activeKey === 'thirdParty'
                              ? '三方平台兑换商品'
                              : '实物资产'}
                        </span>
                        <span>
                          权益类型：
                          {equityTypeMap[item.equityType] || '--'}
                        </span>
                      </div>
                      <div className={styles.itemBody}>
                        <div className={`${styles.cell} ${styles.colProduct}`}>
                          <div className={styles.productThumb}>
                            <Image
                              src={item.productThumbImage}
                              width={70}
                              height={70}
                              preview={{ mask: null }}
                            />
                          </div>
                          <div className={styles.productInfo}>
                            <div
                              className={styles.productTitle}
                              title={item.productName || item.instanceName || '--'}>
                              {item.productName || item.instanceName || '--'}
                            </div>
                            <div className={styles.productBarcode}>商品编码：{item.productBarcode || '--'}</div>
                            <div className={styles.productMeta}>
                              <span className={styles.points}>
                                {item.exchangePoints ? `${item.exchangePoints}积分` : '--'}
                              </span>
                              <span className={styles.quantity}>X{item.quantity || 1}</span>
                            </div>
                          </div>
                        </div>
                        <div className={`${styles.cell} ${styles.colCustomer}`}>
                          <div className={styles.metaLine}>
                            <span className={styles.metaLabel}>用户平台ID：</span>
                            <span className={`${styles.metaValue} ${styles.pinValue}`}>{item.userId || '--'}</span>
                          </div>
                          <div className={styles.metaLine}>
                            <span className={styles.metaLabel}>昵称：</span>
                            <span className={`${styles.metaValue} ${styles.nickValue}`}>
                              {item.userName || item.nickName || item.userNick || '--'}
                            </span>
                          </div>
                        </div>
                        <div className={`${styles.cell} ${styles.colAmount}`}>{item.exchangePoints ?? '--'}</div>
                        <div className={`${styles.cell} ${styles.colStatus}`}>
                          <Tag
                            className={styles.statusTag}
                            color={item.shippingStatus === 'SHIPPED' ? 'green' : 'orange'}>
                            {statusMap[item.shippingStatus] || item.shippingStatus || '--'}
                          </Tag>
                        </div>
                        <div className={`${styles.cell} ${styles.colAction}`}>
                          <Space>
                            {checkPermissions(
                              activeKey === 'coupon' ? ORDER_KEY.COUPON_DETAIL : ORDER_KEY.PRODUCT_DETAIL,
                            ) && (
                              <Button
                                type="link"
                                onClick={() => openDetail(item)}>
                                详情
                              </Button>
                            )}
                            {activeKey === 'physical' && checkPermissions(ORDER_KEY.PRODUCT_SHIP) ? (
                              <Button
                                type="link"
                                disabled={item.shippingStatus === 'SHIPPED'}
                                onClick={() => openShip(item)}>
                                发货
                              </Button>
                            ) : null}
                          </Space>
                        </div>
                      </div>
                    </div>
                  ),
                },
              ],
              [activeKey],
            ) as ColumnsType<any>
          }
        />
      </div>
      <div className={styles.paginationBar}>
        <span className={styles.total}>共 {total} 条</span>
        <Pagination
          current={pageNum}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          showQuickJumper
          onChange={(page, size) => fetchList({ pageNum: page, pageSize: size })}
        />
      </div>

      <Modal
        open={detailVisible}
        title="订单详情"
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={920}>
        {detailData ? (
          <>
            <Steps
              size="small"
              current={1}
              style={{
                padding: '0 200px',
                marginBottom: 16,
              }}
              labelPlacement="vertical">
              <Steps.Step
                title="下单"
                description={detailData.exchangeTime || '--'}
              />
              <Steps.Step title="完成" />
            </Steps>
            <Descriptions
              bordered
              size="small"
              column={3}
              title="订单信息"
              labelStyle={descLabelStyle}
              contentStyle={descContentStyle}>
              <Descriptions.Item label="订单号">{detailData.transactionId || '--'}</Descriptions.Item>
              <Descriptions.Item label="订单状态">
                {statusMap[detailData.shippingStatus] || detailData.shippingStatus || '--'}
              </Descriptions.Item>
              {activeKey !== 'thirdParty' ? (
                <Descriptions.Item label="资产类型">
                  {activeKey === 'coupon' ? '店铺优惠券' : '实物资产'}
                </Descriptions.Item>
              ) : null}
              {activeKey === 'thirdParty' ? (
                <Descriptions.Item label="三方平台券码">{detailData.memberCouponCode || '--'}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label="权益类型">积分兑换</Descriptions.Item>
              <Descriptions.Item label="下单店铺">{getShopDisplayName(detailData.shopId)}</Descriptions.Item>
              <Descriptions.Item label="平台ID">{detailData.userId || '--'}</Descriptions.Item>
              <Descriptions.Item label="客户昵称">{detailData.userNick || '--'}</Descriptions.Item>
              <Descriptions.Item
                span={activeKey === 'thirdParty' ? 1 : 2}
                label="下单时间">
                {detailData.exchangeTime || '--'}
              </Descriptions.Item>
              {activeKey === 'thirdParty' ? (
                <Descriptions.Item label="三方平台订单号">{detailData.acxOrderId || '--'}</Descriptions.Item>
              ) : null}
              <Descriptions.Item
                label="订单备注"
                span={3}>
                {detailData.remark || '--'}
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            {activeKey === 'physical' && (
              <>
                <Descriptions
                  bordered
                  size="small"
                  column={3}
                  title="收货信息"
                  labelStyle={descLabelStyle}
                  contentStyle={descContentStyle}>
                  <Descriptions.Item label="收货人名称">
                    {detailData.deliveryAddress?.receiverName || '--'}
                  </Descriptions.Item>
                  <Descriptions.Item label="手机号">{detailData.deliveryAddress?.phone || '--'}</Descriptions.Item>
                  <Descriptions.Item label="所在地区">
                    {`${detailData.deliveryAddress?.province || ''}${detailData.deliveryAddress?.city || ''}${detailData.deliveryAddress?.district || ''}` ||
                      '--'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="详细地址"
                    span={3}>
                    {`${detailData.deliveryAddress?.province || ''}${detailData.deliveryAddress?.city || ''}${detailData.deliveryAddress?.district || ''}${detailData.deliveryAddress?.detailAddress || ''}` ||
                      '--'}
                  </Descriptions.Item>
                </Descriptions>
                <Divider />
              </>
            )}
            <Descriptions
              bordered
              size="small"
              column={3}
              title="支付信息"
              labelStyle={descLabelStyle}
              contentStyle={descContentStyle}>
              <Descriptions.Item label="支付方式">积分</Descriptions.Item>
              <Descriptions.Item label="交易状态">{detailData.payStatus || '支付完成'}</Descriptions.Item>
              <Descriptions.Item label="支付金额">
                {detailData.payAmount || detailData.exchangePoints || '--'}积分
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            <Descriptions title="订单详情"></Descriptions>
            <Table
              rowKey="id"
              pagination={false}
              dataSource={detailData.items || [{ ...detailData, id: detailData.transactionId }]}
              columns={[
                {
                  title: '商品信息',
                  render: (_: any, rec: any) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Image
                        src={rec.productThumbImage}
                        width={60}
                        height={60}
                        preview={{ mask: null }}
                      />
                      <div>
                        <div>{rec.productName || rec.instanceName || '--'}</div>
                        <div style={{ color: '#999' }}>商品编码：{rec.productBarcode || '--'}</div>
                      </div>
                    </div>
                  ),
                },
                {
                  title: '单价',
                  dataIndex: 'unitExchangePoints',
                  render: (_: any, rec: any) => `${rec.unitExchangePoints ?? rec.exchangePoints ?? '--'}积分`,
                },
                { title: '数量', dataIndex: 'quantity', render: (v: any) => v || 1 },
                {
                  title: '订单金额',
                  render: (_: any, rec: any) => {
                    const unit = Number(rec.unitExchangePoints ?? rec.exchangePoints ?? 0);
                    const qty = Number(rec.quantity ?? 1);
                    const total = Number.isFinite(unit * qty) ? unit * qty : rec.exchangePoints || '--';
                    return `${total}积分`;
                  },
                },
              ]}
            />
          </>
        ) : null}
      </Modal>

      <Modal
        open={shipVisible}
        title="订单发货"
        confirmLoading={shipLoading}
        onCancel={() => setShipVisible(false)}
        onOk={handleShipSubmit}
        okText="提交">
        <Descriptions
          size="small"
          column={3}
          bordered>
          <Descriptions.Item label="收货人名称">{shipTarget?.deliveryAddress?.receiverName || '--'}</Descriptions.Item>
          <Descriptions.Item label="手机号">{shipTarget?.deliveryAddress?.phone || '--'}</Descriptions.Item>
          <Descriptions.Item label="所在地区">
            {`${shipTarget?.deliveryAddress?.province || ''}${shipTarget?.deliveryAddress?.city || ''}${shipTarget?.deliveryAddress?.district || ''}` ||
              '--'}
          </Descriptions.Item>
          <Descriptions.Item
            label="详细地址"
            span={3}>
            {`${shipTarget?.deliveryAddress?.province || ''}${shipTarget?.deliveryAddress?.city || ''}${shipTarget?.deliveryAddress?.district || ''}${shipTarget?.deliveryAddress?.detailAddress || ''}` ||
              '--'}
          </Descriptions.Item>
        </Descriptions>
        <Form
          form={shipForm}
          layout="vertical"
          style={{ marginTop: 16 }}>
          <Form.Item
            label="快递公司"
            name="logisticsCompany">
            <Input
              showCount
              placeholder="请输入"
              maxLength={20}
            />
          </Form.Item>
          <Form.Item
            label="快递单号"
            name="logisticsNumber">
            <Input
              showCount
              placeholder="请输入"
              maxLength={30}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderList;
